import { queryOptions, useMutation } from '@tanstack/react-query';
import type { AiAssistRequest, AiAssistResult, AiStatus } from '@news-cms/shared';
import { ApiError, api } from './api';

/** Drives AI-button visibility (dark-ship): buttons render only when
 *  `enabled` AND the user holds `ai.use`. Refetched infrequently. */
export const aiStatusQueryOptions = queryOptions({
  queryKey: ['ai', 'status'] as const,
  queryFn: () => api<AiStatus>('/api/admin/ai/status'),
  staleTime: 5 * 60_000,
  // A 403 (no ai.use) or any error → treat as unavailable, hide buttons.
  retry: false,
});

export function useAiAssist() {
  return useMutation<AiAssistResult, unknown, AiAssistRequest>({
    mutationFn: (req) =>
      api<AiAssistResult>('/api/admin/ai/assist', { method: 'POST', body: JSON.stringify(req) }),
  });
}

export function aiErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เรียก AI ไม่สำเร็จ ลองใหม่อีกครั้ง';
  switch (e.code) {
    case 'AI_DISABLED':
      return 'ระบบ AI ปิดใช้งานอยู่';
    case 'AI_QUOTA_EXCEEDED':
      return 'ใช้ AI ครบโควตาวันนี้แล้ว ลองใหม่พรุ่งนี้';
    case 'AI_UNCONFIGURED':
      return 'ยังตั้งค่า AI ไม่ครบ (ติดต่อผู้ดูแลระบบ)';
    case 'AI_TIMEOUT':
      return 'AI ตอบช้าเกินไป ลองใหม่อีกครั้ง';
    case 'AI_UPSTREAM':
    case 'AI_BAD_OUTPUT':
      return 'AI มีปัญหาชั่วคราว ลองใหม่อีกครั้ง';
    case 'FORBIDDEN':
      return 'ไม่มีสิทธิ์ใช้ AI';
    default:
      return e.message || 'เรียก AI ไม่สำเร็จ';
  }
}

/** Strip HTML → plaintext for feeding the editor body to AI (which reasons on
 *  text, not markup). Mirrors the server's loose tag-strip. */
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  return html
    .replace(/<\/(p|h[1-6]|li|blockquote|pre|br)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim();
}
